import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { get, set, del } from "idb-keyval";
import { fetchData, LanguageEnum } from "dmsbasereact";

const CACHE_PREFIX = "help-center";
const CACHE_TTL_HOURS = 24;

const getLanguageOrDefault = (language) => language || LanguageEnum.PT_BR;
const normalizeSlug = (slug) => (slug ? slug : "index");
const now = () => new Date();

export const useHelpCenterData = ({ slug, route, language, enabled }) => {
  const resolvedSlug = normalizeSlug(slug);
  const normalizedRoute = route || "/";
  const normalizedLanguage = getLanguageOrDefault(language);
  const cacheKey = `${CACHE_PREFIX}:${normalizedLanguage}:${resolvedSlug}`;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== "undefined" ? !navigator.onLine : false
  );

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const handleOnline = () => {
      setIsOffline(false);
    };
    const handleOffline = () => {
      setIsOffline(true);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      mountedRef.current = false;
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const loadFromCache = useCallback(async () => {
    try {
      const cached = await get(cacheKey);
      if (!cached || !cached.data || !mountedRef.current) {
        return null;
      }
      setData(cached.data);
      setFromCache(true);
      if (cached.updatedAt) {
        setLastUpdated(new Date(cached.updatedAt));
      }
      return cached.data;
    } catch {
      return null;
    }
  }, [cacheKey]);

  const fetchContext = useCallback(
    async (options = { skipCache: false }) => {
      if (!enabled) {
        return null;
      }
      const { skipCache } = options;
      setLoading(true);
      setError(null);

      let cachedData = null;
      if (!skipCache) {
        cachedData = await loadFromCache();
      }

      const shouldAttemptNetwork =
        typeof navigator === "undefined" || navigator.onLine;
      if (!shouldAttemptNetwork) {
        setLoading(false);
        return cachedData;
      }

      try {
        const response = await fetchData(
          `/help-center/context?route=${encodeURIComponent(
            normalizedRoute
          )}&slug=${encodeURIComponent(resolvedSlug)}&language=${normalizedLanguage}`,
          "GET",
          true
        );
        const payload = response?.data ?? response;
        if (!mountedRef.current) {
          return payload;
        }
        const timestamp = now();
        setData(payload);
        setFromCache(false);
        setLastUpdated(timestamp);
        await set(cacheKey, {
          data: payload,
          updatedAt: timestamp.toISOString(),
        });
        return payload;
      } catch (err) {
        if (!cachedData) {
          setError(err);
        }
        return cachedData;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [
      cacheKey,
      enabled,
      loadFromCache,
      normalizedLanguage,
      normalizedRoute,
      resolvedSlug,
    ]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    fetchContext({ skipCache: false });
  }, [enabled, fetchContext]);

  const refresh = useCallback(async () => {
    await del(cacheKey);
    return fetchContext({ skipCache: true });
  }, [cacheKey, fetchContext]);

  const isStale = useMemo(() => {
    if (!lastUpdated) {
      return true;
    }
    const ttlMs = CACHE_TTL_HOURS * 60 * 60 * 1000;
    return now().getTime() - lastUpdated.getTime() > ttlMs;
  }, [lastUpdated]);

  return {
    data,
    loading,
    error,
    refresh,
    fromCache,
    lastUpdated,
    isOffline,
    isStale,
    search: () => [],
  };
};
